'use client';

/**
 * Composant générique d'affichage des résultats avec render prop
 *
 * Principe SOLID: Dependency Inversion
 * - Dépend d'une abstraction (render prop function)
 * - Pas de dépendance aux implémentations concrètes des cartes
 *
 * Principe Open/Closed:
 * - Ouvert à l'extension via render prop
 * - Fermé à la modification
 *
 * @module EntityHits
 */

import type { BaseHit } from 'instantsearch.js';
import React from 'react';
import { useHits } from 'react-instantsearch';

// ============================================================================
// Types
// ============================================================================

export interface EntityHitsProps<T extends BaseHit> {
  /**
   * Fonction render prop pour afficher une carte
   * Permet la customisation complète du rendu
   *
   * @param hit - Résultat de recherche
   */
  renderHitCard: (hit: T) => React.ReactNode;
}

// ============================================================================
// Skeleton Component
// ============================================================================

/**
 * Skeleton de chargement pour les résultats
 * Grid 5 colonnes (identique au layout des résultats)
 */
function EntityHitsSkeleton() {
  return (
    <div className="grid w-full grid-flow-row-dense auto-rows-auto grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
          <div className="flex flex-col gap-3">
            {/* Image placeholder */}
            <div className="aspect-[12/15] w-full rounded bg-gray-200" />
            {/* Text placeholders */}
            <div className="h-4 w-3/4 rounded bg-gray-200" />
            <div className="h-3 w-1/2 rounded bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

/**
 * Affichage générique des résultats avec render prop
 *
 * Layout: Grid 5 colonnes (comme l'original)
 * - grid-cols-2 sur mobile
 * - grid-cols-3 sur sm/md
 * - grid-cols-5 sur lg
 *
 * Bénéfices du pattern render prop:
 * - ✅ Aucune dépendance aux types d'entités
 * - ✅ Customisation complète du rendu des cartes
 * - ✅ Un seul composant pour toutes les entités
 * - ✅ Pas de if/switch sur le type d'entité
 *
 * @example
 * ```tsx
 * <EntityHits
 *   renderHitCard={(hit) => <AuteurCard auteur={hit} />}
 * />
 * ```
 */
export function EntityHits<T extends BaseHit>({
  renderHitCard,
}: EntityHitsProps<T>) {
  const { items, results } = useHits<T>();

  // Loading state
  if (!results) {
    return <EntityHitsSkeleton />;
  }

  // Empty state géré par NoResults dans EntitySearchResults

  return (
    <div
      className="grid w-full grid-flow-row-dense auto-rows-auto grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5"
      id="search-results-main"
    >
      {items.map((hit: any) => (
        <React.Fragment key={hit.id || hit.slug}>
          {renderHitCard(hit)}
        </React.Fragment>
      ))}
    </div>
  );
}
